// QLDMP 模块：
// 负责快捷启动整层数据的请求、分帧重组、解析与本地缓存同步。
export function createQldmpModule(options) {
    const {
        deviceAdapter,
        reportId,
        createDataHeader,
        sendReportTime,
        getNowTime,
        qldmpSessions,
        qldmpWaiters,
        quickLaunchStorageKey = 'layerQuickLaunchDump',
        quickLaunchViewStorageKey = 'layerQuickLaunchViewData'
    } = options

    // 读取快捷启动原始缓存（按 layer 存储）。
    function getQldmpStorage() {
        try {
            const raw = localStorage.getItem(quickLaunchStorageKey)
            if (!raw) return {}
            const parsed = JSON.parse(raw)
            return parsed && typeof parsed === 'object' ? parsed : {}
        } catch (error) {
            console.warn('[QLDMP] 读取本地存储失败:', error)
            return {}
        }
    }

    // 写入快捷启动原始缓存。
    function saveQldmpStorage(nextData) {
        try {
            localStorage.setItem(quickLaunchStorageKey, JSON.stringify(nextData))
        } catch (error) {
            console.warn('[QLDMP] 写入本地存储失败:', error)
        }
    }

    // 规范化单条快捷启动，确保字段完整且类型稳定。
    function normalizeQuickLaunchEntry(entry) {
        const uid = Number(entry?.uid)
        const serial = Number(entry?.serial)
        const type = String(entry?.type || '').toLowerCase()
        const value = String(entry?.value ?? '')
        const allowedTypes = ['url', 'text', 'file']
        return {
            uid: Number.isInteger(uid) ? uid : 0,
            keycode: Number.isInteger(uid) ? uid : 0,
            serial: Number.isInteger(serial) ? serial : 0,
            macroId: Number.isInteger(serial) && serial > 0 ? `M${serial}` : 'M0',
            type: allowedTypes.includes(type) ? type : 'url',
            value
        }
    }

    // 生成给界面使用的快捷启动视图数据（0~3层固定结构）。
    function buildLayerQuickLaunchViewData(rawStorage = null) {
        const source = rawStorage && typeof rawStorage === 'object' ? rawStorage : getQldmpStorage()
        const result = { 0: [], 1: [], 2: [], 3: [] }
        for (let layer = 0; layer <= 3; layer++) {
            const list = Array.isArray(source[layer]) ? source[layer] : []
            result[layer] = list.map(normalizeQuickLaunchEntry)
        }
        return result
    }

    // 同步写入界面缓存，保证页面回显直接可用。
    function syncLayerQuickLaunchViewStorage() {
        const viewData = buildLayerQuickLaunchViewData()
        try {
            localStorage.setItem(quickLaunchViewStorageKey, JSON.stringify(viewData))
        } catch (error) {
            console.warn('[QLDMP] 写入快捷启动视图缓存失败:', error)
        }
        return viewData
    }

    // 保存某一层快捷启动并更新视图缓存。
    function saveLayerQuickLaunchToStorage(layer, entries) {
        const normalizedLayer = Number(layer) || 0
        const storage = getQldmpStorage()
        storage[normalizedLayer] = Array.isArray(entries) ? entries : []
        saveQldmpStorage(storage)
        syncLayerQuickLaunchViewStorage()
    }

    // 结束某层所有等待者（fetchLayerQuickLaunchDump）。
    function resolveQldmpWaiters(layer, payload) {
        const waiters = qldmpWaiters.value.get(layer)
        if (!waiters?.length) return

        qldmpWaiters.value.delete(layer)
        waiters.forEach(({ resolve, timer }) => {
            clearTimeout(timer)
            resolve(payload)
        })
    }

    // 处理 QLDMP 回包：
    // - 识别头帧/续帧
    // - 分帧聚合
    // - 拼接 JSON
    // - 同步缓存并唤醒等待者
    function tryHandleQldmpPacket(uint8Array) {
        if (!uint8Array || uint8Array.length < 10) return false

        let offset = -1
        const cmd = [0x51, 0x4c, 0x44, 0x4d, 0x50]
        const matchAt = (start) => cmd.every((v, i) => uint8Array[start + i] === v)
        if (matchAt(0)) offset = 0
        else if (uint8Array[0] === 0x04 && uint8Array.length >= 11 && matchAt(1)) offset = 1
        if (offset < 0) return false

        const layer = Number(uint8Array[offset + 5])
        if (!Number.isInteger(layer) || layer < 0 || layer > 3) return true

        const isHeadFrame = uint8Array.length >= (offset + 11) && Number(uint8Array[offset + 9]) === 1

        if (isHeadFrame) {
            const totalLen = Number(uint8Array[offset + 6]) | (Number(uint8Array[offset + 7]) << 8)
            const entryCount = Number(uint8Array[offset + 8])
            const seq = Number(uint8Array[offset + 9])
            const total = Number(uint8Array[offset + 10])
            const body = uint8Array.slice(offset + 11)
            qldmpSessions.value.set(layer, {
                totalLen,
                entryCount,
                total,
                chunks: new Map([[seq, body]])
            })
        } else {
            const seq = Number(uint8Array[offset + 6])
            const total = Number(uint8Array[offset + 7])
            const body = uint8Array.slice(offset + 8)
            const session = qldmpSessions.value.get(layer)
            if (!session) return true
            session.total = total
            session.chunks.set(seq, body)
        }

        const session = qldmpSessions.value.get(layer)
        if (!session || !session.total || session.chunks.size < session.total) return true

        const merged = new Uint8Array(session.totalLen)
        let writeOffset = 0
        for (let i = 1; i <= session.total; i++) {
            const part = session.chunks.get(i)
            if (!part) return true
            const remain = session.totalLen - writeOffset
            if (remain <= 0) break
            const copyLength = Math.min(remain, part.length)
            merged.set(part.slice(0, copyLength), writeOffset)
            writeOffset += copyLength
        }

        let parsed = []
        try {
            const text = new TextDecoder('utf-8').decode(merged.slice(0, session.totalLen))
            const json = JSON.parse(text)
            parsed = Array.isArray(json) ? json : []
        } catch (error) {
            console.warn(`[QLDMP] layer ${layer} 解析失败，降级为空数组:`, error)
            parsed = []
        }

        qldmpSessions.value.delete(layer)
        saveLayerQuickLaunchToStorage(layer, parsed)
        resolveQldmpWaiters(layer, {
            layer,
            entries: parsed,
            entryCount: session.entryCount
        })
        return true
    }

    // 发送单层 QLDMP 请求。
    async function sendLayerQuickLaunch(layer) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }
        const headerIdentifier = [0x43, 0x52, 0x54, 0x00, 0x00, 0x51, 0x4c, 0x44, 0x4d, 0x50]
        const header = createDataHeader(headerIdentifier, 0)
        header[10] = Number(layer) || 0
        await deviceAdapter.sendReport(reportId, header)
        sendReportTime.value = getNowTime()
    }

    // 请求单层快捷启动并等待回包完成（含超时处理）。
    async function fetchLayerQuickLaunchDump(layer = 0, timeout = 3000) {
        if (!deviceAdapter.isOpened) {
            throw new Error('设备未连接')
        }

        const normalizedLayer = Number(layer) || 0
        qldmpSessions.value.delete(normalizedLayer)

        const waitPromise = new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                const nextWaiters = (qldmpWaiters.value.get(normalizedLayer) || []).filter(item => item.resolve !== resolve)
                if (nextWaiters.length) {
                    qldmpWaiters.value.set(normalizedLayer, nextWaiters)
                } else {
                    qldmpWaiters.value.delete(normalizedLayer)
                }
                reject(new Error(`QLDMP layer ${normalizedLayer} 超时`))
            }, timeout)

            const currentWaiters = qldmpWaiters.value.get(normalizedLayer) || []
            currentWaiters.push({ resolve, reject, timer })
            qldmpWaiters.value.set(normalizedLayer, currentWaiters)
        })

        await sendLayerQuickLaunch(normalizedLayer)
        return await waitPromise
    }

    // 同步所有层快捷启动到本地缓存。
    // 当前按项目约定仅拉取 layer 0。
    async function syncAllLayerQuickLaunchToStorage(timeout = 4000) {
        const result = {}
        for (let layer = 0; layer <= 0; layer++) {
            try {
                const payload = await fetchLayerQuickLaunchDump(layer, timeout)
                result[layer] = payload.entries || []
            } catch (error) {
                console.warn(`[QLDMP] 同步 layer ${layer} 失败:`, error)
                result[layer] = []
                saveLayerQuickLaunchToStorage(layer, [])
            }
        }

        syncLayerQuickLaunchViewStorage()
        return result
    }

    return {
        tryHandleQldmpPacket,
        sendLayerQuickLaunch,
        fetchLayerQuickLaunchDump,
        syncAllLayerQuickLaunchToStorage,
        buildLayerQuickLaunchViewData,
        syncLayerQuickLaunchViewStorage
    }
}

